<script setup lang="ts">
import { computed, ConcreteComponent, defineProps, ref, withDefaults } from 'vue'
import BaseTablePagination from './BaseTablePagination.vue'

export interface DataTableHeader {
    text: string
    value?: string
    classes?: string
    component?: ConcreteComponent
    valueFormatter?: (value) => any
    itemFormatter?: (item) => any
    showHeader?: boolean
}

interface Props {
    items: any[]
    headers: DataTableHeader[]
    paginate?: boolean
    perPage?: number
}
const props = withDefaults(defineProps<Props>(), {
    paginate: false,
    perPage: 10,
})

const currentPage = ref(1)

const rangeBottom = computed(() => (currentPage.value - 1) * props.perPage + 1)
const rangeTop = computed(() => {
    const tempTop = rangeBottom.value + props.perPage - 1
    return tempTop <= props.items.length ? tempTop : props.items.length
})

const visibleItems = computed(() => {
    if (props.paginate) {
        return props.items.filter((item, index) => index + 1 >= rangeBottom.value && index + 1 <= rangeTop.value)
    }
    return props.items
})

function getValue(header: DataTableHeader, item) {
    if (header.valueFormatter) {
        return header.valueFormatter(item[header.value])
    } else if (header.itemFormatter) {
        return header.itemFormatter(item)
    }
    return item[header.value]
}
</script>

<template>
    <div>
        <table class="min-w-full divide-y divide-gray-300">
            <thead>
                <tr>
                    <template v-for="(header, idx) in props.headers" :key="idx">
                        <th
                            scope="col"
                            class="py-3.5 text-left text-sm font-semibold text-gray-900"
                            :class="[
                                { 'pl-4 sm:pl-6': idx === 0, 'pr-3 sm:pl-6': idx === props.headers.length - 1 },
                                header.classes,
                            ]">
                            <span v-if="header.showHeader === false" class="sr-only">{{ header.text }}</span>
                            <template v-else>{{ header.text }}</template>
                        </th>
                    </template>
                </tr>
            </thead>
            <tbody class="bg-white">
                <tr v-for="(item, idx) in visibleItems" :key="idx" class="even:bg-gray-50">
                    <td
                        v-for="(header, idxx) in props.headers"
                        :key="idxx"
                        class="px-3 py-4 text-sm text-gray-900"
                        :class="[
                            { 'pl-4 sm:pl-6': idxx === 0, 'pr-3 sm:pl-6': idxx === props.headers.length - 1 },
                            header.classes,
                        ]">
                        <slot :name="`item.${header.value}`" :item="item" :value="getValue(header, item)">
                            {{ getValue(header, item) }}
                        </slot>
                    </td>
                </tr>
            </tbody>
        </table>
        <BaseTablePagination
            v-if="props.paginate && props.perPage < props.items.length"
            ref="pagination"
            v-model="currentPage"
            :total-count="props.items.length"
            :per-page="props.perPage" />
    </div>
</template>
